import React, { useEffect, useMemo, useState } from "react";
import { Tabs, notification } from "antd";
import { useHistory, useLocation } from "react-router-dom";
import { getPermissionsForUser } from "constants/permission";

/**
 * BasicTabs
 * @param {Array} tabs
 * @param {Object} item
 {
    key: AccreditationSettingKey, // key
    label :translation("accreditation.setting"),  // title
    permission: ALL_PERMISSION.MANAGE_RECORD.permissionTab.TAB_OPERATION_SETTINGS.value,  // quyền, có thể bỏ trống nhưng ko đc khác trong const
    pathname: '/accreditation-setting', // nếu muốn link trang mới
    condition: [STATION_TYPES.MAIN_STATION], // cái này là ràng buộc thêm điều kiện hiển thị
    component: <AccreditationSetting /> // conponent của nó
  },
 * @returns
 */

const BasicTabs = ({
  tabs = [],
  permissionConstant = {},
  translation,
  checkByPathname,
}) => {
  const history = useHistory();
  const location = useLocation();
  const userPermissions = getPermissionsForUser();

  const validPermissions = useMemo(
    () => Object.values(permissionConstant).map((p) => p.value),
    [permissionConstant]
  );

  // Parse query param
  const getTabFromQuery = () => {
    const params = new URLSearchParams(location.search);
    return params.get("tab");
  };

  // Filter tabs by user permissions + condition + const validation
  const filteredTabs = useMemo(() => {
    return tabs.filter((tab) => {
      const { permission, condition, label, isHidden } = tab;

      if (isHidden) {
        return false;
      }

      if (permission && !userPermissions.includes(permission)) {
        return false;
      }

      if (
        Array.isArray(condition) &&
        condition.length > 0 &&
        !condition.every(Boolean)
      ) {
        return false;
      }

      if (permission && !validPermissions.includes(permission)) {
        notification.error({
          message: "Lỗi cấu hình quyền",
          description: `Tab "${label}" có quyền không tồn tại trong constant: "${permission}"`,
        });
      }

      return true;
    });
  }, [tabs, validPermissions, userPermissions]);

  // Initial active tab logic (with checkByPathname)
  const tabParamKey = getTabFromQuery();
  const initialKey =
    (checkByPathname &&
      filteredTabs.find((tab) => tab.pathname === location.pathname)?.key) ||
    filteredTabs.find((tab) => tab.key === location.pathname)?.key ||
    filteredTabs.find((tab) => tab.key === tabParamKey)?.key ||
    filteredTabs[0]?.key;

  const [activeKey, setActiveKey] = useState(initialKey);

  // Update active tab when route or query changes
  useEffect(() => {
    const tabParamKey = getTabFromQuery();

    const keyFromCheckPathname =
      checkByPathname &&
      filteredTabs.find((tab) => tab.pathname === location.pathname)?.key;

    const keyFromPath = filteredTabs.find(
      (tab) => tab.key === location.pathname
    )?.key;
    const keyFromQuery = filteredTabs.find(
      (tab) => tab.key === tabParamKey
    )?.key;

    const newKey =
      keyFromCheckPathname ||
      keyFromPath ||
      keyFromQuery ||
      filteredTabs[0]?.key;
    setActiveKey(newKey);
  }, [location.pathname, location.search, filteredTabs, checkByPathname]);

  const handleChange = (key) => {
    setActiveKey(key);
    const targetTab = filteredTabs.find((tab) => tab.key === key);

    if (targetTab?.pathname) {
      history.replace(targetTab.pathname);
    } else {
      const currentParams = new URLSearchParams(location.search);
      currentParams.set("tab", key);
      history.replace(`${location.pathname}?${currentParams.toString()}`);
    }
  };

  return (
    <>
      <Tabs activeKey={activeKey} onChange={handleChange}>
        {filteredTabs.map((tab) => (
          <Tabs.TabPane
            tab={translation ? translation(tab.label) : tab.label}
            key={tab.key}
          />
        ))}
      </Tabs>

      {filteredTabs.map((tab) => {
        const isLocalTab = !tab.pathname;
        return isLocalTab && tab.key === activeKey ? (
          <div key={tab.key}>{tab.component}</div>
        ) : null;
      })}
    </>
  );
};

export default BasicTabs;
